import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Camera } from "../../lib/api";
import {
  parseSelectionJSON,
  parseSelectionParam,
  reconcileSelection,
  seedSelection,
  serializeSelectionParam,
  toggleSelection,
} from "./liveSelection";

const STORAGE_KEY = "mtn-cctv-live-selection";
const CAMS_PARAM = "cams";

function readStoredSelection(): readonly string[] | null {
  try {
    return parseSelectionJSON(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private browsing / storage disabled: fall through to "never chosen".
    return null;
  }
}

function writeStoredSelection(selection: readonly string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Best-effort: the in-memory state still updates for this session.
  }
}

export interface LiveSelection {
  /** The reconciled selection — always a subset of the cameras currently
   *  loaded. Empty (and `isReady` false) until the camera list resolves. */
  selection: readonly string[];
  /** False during the initial camera-list load, when reconciling or seeding
   *  against an empty list would be premature and, for the seed, would
   *  wrongly persist as "chose nothing" (see the seeding effect below). */
  isReady: boolean;
  toggle: (cameraId: string) => void;
}

/**
 * Binds the pure logic in liveSelection.ts to its two homes: the `cams` URL
 * param (source of truth — a wall is shareable and reloadable by link, and a
 * control room can pin every screen to the same one, preserving the
 * one-RTSP-pull-per-camera-per-link property autoStart.ts documents) and
 * localStorage (fallback, restoring a browser's own last wall when no link
 * was given).
 *
 * `cameras` gates both seeding and reconciliation on having actually loaded
 * — the same guard LiveGridPage already applies to its own project-code
 * lookup, and for the same reason: acting on a query that hasn't resolved
 * yet computes the empty list, and for a first-run seed that would get
 * persisted as "chose nothing", permanently suppressing the real seed.
 */
export function useLiveSelection(cameras: readonly Camera[] | undefined, limit: number, autoStartCount: number): LiveSelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const camsParam = searchParams.get(CAMS_PARAM);

  // Raw (unreconciled) selection: the URL wins when a `cams` param is
  // present (even an empty one — that is "chose nothing", not "never
  // chosen"), else localStorage, else null ("never chosen", seeded below
  // once the camera list loads). Read once at mount, the house pattern for
  // a persisted preference (see useGridColumns/useVideoFit) — subsequent
  // changes flow through `persist`, not by re-reading the URL.
  const [rawSelection, setRawSelection] = useState<readonly string[] | null>(() =>
    camsParam !== null ? parseSelectionParam(camsParam) : readStoredSelection(),
  );

  const persist = useCallback(
    (next: readonly string[]) => {
      setRawSelection(next);
      writeStoredSelection(next);
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set(CAMS_PARAM, serializeSelectionParam(next));
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Persisted immediately, not just held in memory: without this, a brand
  // new browser's default wall would vanish from the URL and localStorage
  // the moment the tab closes unless the operator happened to tick or
  // untick something first — reseeding on the next visit produces the same
  // first N cameras, so the gap is invisible for one viewer, but it breaks
  // sharing THIS wall's link before it has ever been touched.
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (cameras === undefined) return;
    if (rawSelection !== null) return;
    if (hasSeededRef.current) return;
    hasSeededRef.current = true;
    const enabled = cameras.filter((c) => c.enabled);
    persist(seedSelection(enabled, autoStartCount, limit));
  }, [cameras, rawSelection, autoStartCount, limit, persist]);

  const availableIds = useMemo(() => new Set((cameras ?? []).map((c) => c.id)), [cameras]);

  const selection = useMemo(() => {
    if (cameras === undefined || rawSelection === null) return [];
    return reconcileSelection(rawSelection, availableIds);
  }, [cameras, rawSelection, availableIds]);

  const toggle = useCallback(
    (cameraId: string) => {
      persist(toggleSelection(selection, cameraId, limit));
    },
    [selection, limit, persist],
  );

  return { selection, isReady: cameras !== undefined && rawSelection !== null, toggle };
}
